import { Canvas, type RootState } from '@react-three/fiber';
import { Suspense } from 'react';
import type { WebGLRenderer } from 'three';
import { Scene } from './Scene';
import { useWebGPUSupport } from '@/hooks/useWebGPUSupport';
import { useSettingsStore } from '@/store/useSettingsStore';
import { XRWrapper } from '@/xr/XRWrapper';

/**
 * Root <Canvas> wrapper with WebGPU detection and WebGL2 fallback.
 *
 * - WebGPU is attempted when `navigator.gpu` is available.
 * - Force a backend with `?renderer=webgl` or `?renderer=webgpu`.
 * - If WebGPU `init()` fails at runtime, reload with `?renderer=webgl`.
 */
export function Renderer() {
  const useWebGPU = useWebGPUSupport();
  const dpr = useSettingsStore((s) => s.dpr);
  const shadows = useSettingsStore((s) => s.shadows);

  // The async gl factory is typed loosely because R3F's `GLProps` type
  // expects a WebGL2 renderer; WebGPURenderer is API-compatible at runtime
  // but not by type. Cast at the boundary.
  const glFactory = useWebGPU
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      async (props: any) => {
        const { WebGPURenderer } = await import('three/webgpu');
        const renderer = new WebGPURenderer(props);
        await renderer.init();
        // eslint-disable-next-line no-console
        console.info('[Renderer] WebGPU active');
        return renderer as unknown as WebGLRenderer;
      }
    : undefined;

  return (
    <Canvas
      shadows={shadows}
      dpr={dpr}
      camera={{ position: [0, 0.5, 9], fov: 45, near: 0.1, far: 200 }}
      gl={glFactory}
      onCreated={(_state: RootState) => {
        if (!useWebGPU) {
          // eslint-disable-next-line no-console
          console.info('[Renderer] WebGL2 active. Add ?renderer=webgpu to try WebGPU.');
        }
      }}
    >
      <Suspense fallback={null}>
        <XRWrapper>
          <Scene />
        </XRWrapper>
      </Suspense>
    </Canvas>
  );
}
